import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Chip, Collapse, IconButton, Box, Button, Stack, CircularProgress, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GroupIcon from '@mui/icons-material/Group';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import EquipesTempoSaidaBaseModal from './EquipesTempoSaidaBaseModal';

const getTempoColor = (valor) => {
  if (valor <= 30) return 'success'; // verde
  if (valor <= 60) return 'warning'; // amarelo
  return 'error'; // vermelho
};

const DetalheEquipeTable = ({ equipes, isDark }) => {
  const tableBg = isDark ? 'rgba(255, 255, 255, 0.05)' : '#f9f9f9';
  const sortedEquipes = [...equipes].sort((a, b) => {
    const aVal = typeof a.tempo_medio_saida_base_minutos === 'number' ? a.tempo_medio_saida_base_minutos : 99999;
    const bVal = typeof b.tempo_medio_saida_base_minutos === 'number' ? b.tempo_medio_saida_base_minutos : 99999;
    return aVal - bVal;
  });

  const getRankColor = (idx) => {
    if (idx === 0 || idx === 1 || idx === 2) return 'success.main'; // Menores tempos em verde
    else if (idx >= 3 && idx <= 5) return 'warning.main'; // Posições médias em amarelo
    else return 'error.main'; // Maiores tempos em vermelho
  };

  return (
    <Table size="small" sx={{ mt: 1, mb: 1, background: tableBg, borderRadius: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, textAlign: 'center', width: 60 }}>#</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Equipe</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Tempo Médio de Saída da Base (min)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedEquipes.map((equipe, idx) => (
          <TableRow key={equipe.equipe || idx}>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: 16, color: getRankColor(idx) }}>
              {idx + 1}
            </TableCell>
            <TableCell>{equipe.equipe}</TableCell>
            <TableCell>
              <Chip
                label={typeof equipe.tempo_medio_saida_base_minutos === 'number' ? `${equipe.tempo_medio_saida_base_minutos.toFixed(2)} min` : '--'}
                color={getTempoColor(equipe.tempo_medio_saida_base_minutos)}
                size="small"
                sx={{ fontWeight: 700, fontSize: 15, minWidth: 80 }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const TempoSaidaBaseRankingTable = ({ data = [], startDate, endDate, contrato = 'Todos', centro = 'Todos', equipe = 'Todos', loading = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [openRows, setOpenRows] = useState({});
  const [detalhes, setDetalhes] = useState({});
  const [loadingDetalhe, setLoadingDetalhe] = useState({});
  const [exportando, setExportando] = useState(false);
  const [equipesModalOpen, setEquipesModalOpen] = useState(false);

  // Ordena do menor para o maior tempo
  const sorted = [...data].sort((a, b) => {
    const aVal = typeof a.tempo_medio_saida_base_minutos === 'number' ? a.tempo_medio_saida_base_minutos : 99999;
    const bVal = typeof b.tempo_medio_saida_base_minutos === 'number' ? b.tempo_medio_saida_base_minutos : 99999;
    return aVal - bVal;
  });

  const handleExpand = async (contrato) => {
    setOpenRows((prev) => ({ ...prev, [contrato]: !prev[contrato] }));
    if (!detalhes[contrato] && !loadingDetalhe[contrato]) {
      setLoadingDetalhe((prev) => ({ ...prev, [contrato]: true }));
      try {
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const formatDate = (date) => {
          if (!date) return '';
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const dd = String(date.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };
        const payload = {
          des_contrato: contrato,
          'data-referencia-inicio': formatDate(startDate),
          'data-referencia-fim': formatDate(endDate)
        };
        const resp = await fetch(`${apiUrl}/tempo-medio-saida-base-equipe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json();
        setDetalhes((prev) => ({ ...prev, [contrato]: Array.isArray(data) ? data : [] }));
      } catch {
        setDetalhes((prev) => ({ ...prev, [contrato]: [] }));
      } finally {
        setLoadingDetalhe((prev) => ({ ...prev, [contrato]: false }));
      }
    }
  };

  const exportToPDF = async (sorted) => {
    setExportando(true);
    // Buscar detalhes de equipes para todos os contratos
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const formatDate = (date) => {
      if (!date) return '';
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const payloadBase = (contrato) => ({
      des_contrato: contrato,
      'data-referencia-inicio': formatDate(startDate),
      'data-referencia-fim': formatDate(endDate)
    });
    // Busca todos os detalhes em paralelo
    const detalhesAll = {};
    await Promise.all(sorted.map(async (row) => {
      try {
        const resp = await fetch(`${apiUrl}/tempo-medio-saida-base-equipe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadBase(row.des_contrato))
        });
        const data = await resp.json();
        detalhesAll[row.des_contrato] = Array.isArray(data) ? data : [];
      } catch {
        detalhesAll[row.des_contrato] = [];
      }
    }));
    // Padronização de fontes
    const FONT_TITLE = 16;
    const FONT_HEADER = 12;
    const FONT_BODY = 11;
    const FONT_SUBTITLE = 12;
    const doc = new jsPDF();
    doc.setFontSize(FONT_TITLE);
    doc.text('Tempo Médio de Saída da Base', 14, 18);
    // Tabela principal
    const mainTableBody = sorted.map((row, idx) => {
      let rankColor = '#000';
      if (idx === 3 || idx === 4) rankColor = '#2e7d32'; // verde
      else if (idx >= 5 && idx <= 7) rankColor = '#ed6c02'; // amarelo
      else if (idx >= 8) rankColor = '#d32f2f'; // vermelho
      let rank = idx + 1;
      return [
        { content: rank, styles: { textColor: rankColor, fontStyle: idx < 3 ? 'bold' : 'normal', fontSize: FONT_BODY } },
        row.des_contrato,
        typeof row.tempo_medio_saida_base_minutos === 'number' ? row.tempo_medio_saida_base_minutos.toFixed(2) + ' min' : '--'
      ];
    });
    autoTable(doc, {
      startY: 24,
      head: [['Ranking', 'Contrato', 'Tempo Médio de Saída da Base (min)']],
      body: mainTableBody,
      theme: 'grid',
      styles: { fontSize: FONT_BODY },
      headStyles: { fillColor: [22, 160, 133], fontSize: FONT_HEADER },
      margin: { left: 14, right: 14 },
      didDrawCell: (data) => {
        if (data.row.index < sorted.length - 1) {
          data.cell.height += 2;
        }
      }
    });
    let y = doc.lastAutoTable.finalY + 4;
    // Sub-tabelas de equipes
    sorted.forEach((row, idx) => {
      const equipes = detalhesAll[row.des_contrato] || [];
      doc.setFontSize(FONT_SUBTITLE);
      doc.text(`Contrato: ${row.des_contrato} - Equipes`, 14, y);
      y += 4;
      if (equipes.length > 0) {
        const equipesOrdenadas = [...equipes].sort((a, b) => {
          const aVal = typeof a.tempo_medio_saida_base_minutos === 'number' ? a.tempo_medio_saida_base_minutos : 99999;
          const bVal = typeof b.tempo_medio_saida_base_minutos === 'number' ? b.tempo_medio_saida_base_minutos : 99999;
          return aVal - bVal;
        });
        autoTable(doc, {
          startY: y,
          head: [[
            'Equipe', 'Tempo Médio de Saída da Base (min)'
          ]],
          body: equipesOrdenadas.map(eq => [
            eq.equipe,
            typeof eq.tempo_medio_saida_base_minutos === 'number' ? eq.tempo_medio_saida_base_minutos.toFixed(2) + ' min' : '--'
          ]),
          theme: 'grid',
          styles: { fontSize: FONT_BODY },
          headStyles: { fillColor: [100, 181, 246], fontSize: FONT_HEADER },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 8;
      } else {
        doc.setFontSize(FONT_BODY);
        doc.text('Sem dados de equipes.', 18, y);
        y += 8;
      }
    });
    doc.save('tempo_saida_base_ranking.pdf');
    setExportando(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
        <CircularProgress size={48} thickness={4} />
      </Box>
    );
  }

  return (
    <>
    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Tempo Médio de Saída da Base
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<GroupIcon />}
            onClick={() => setEquipesModalOpen(true)}
            sx={{ fontWeight: 600 }}
          >
            Equipes
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => exportToPDF(sorted)}
            sx={{ fontWeight: 600 }}
            disabled={exportando}
          >
            {exportando ? 'Exportando...' : 'Exportar PDF'}
          </Button>
        </Stack>
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Tempo Médio de Saída da Base (min)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography color="text.secondary">Nenhum dado encontrado.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((row, idx) => {
              let rankColor = undefined;
              let rankContent = idx + 1;
              // Cores para ranking - menores tempos são melhores
              if (idx === 0 || idx === 1 || idx === 2) rankColor = 'success.main'; // Top 3 em verde
              else if (idx >= 3 && idx <= 6) rankColor = 'warning.main'; // Posições 4-7 em amarelo
              else if (idx >= 7) rankColor = 'error.main'; // Posições 8+ em vermelho
              return (
                <React.Fragment key={row.des_contrato || idx}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => handleExpand(row.des_contrato)}
                      >
                        {openRows[row.des_contrato] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 20, color: rankColor }}>
                      {rankContent}
                    </TableCell>
                    <TableCell>{row.des_contrato}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={typeof row.tempo_medio_saida_base_minutos === 'number' ? `${row.tempo_medio_saida_base_minutos.toFixed(2)} min` : '--'}
                        color={getTempoColor(row.tempo_medio_saida_base_minutos)}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 15, minWidth: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                      <Collapse in={openRows[row.des_contrato]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          {loadingDetalhe[row.des_contrato] ? (
                            <Typography color="text.secondary">Carregando...</Typography>
                          ) : (
                            <DetalheEquipeTable equipes={detalhes[row.des_contrato] || []} isDark={isDark} />
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    
    <EquipesTempoSaidaBaseModal
      open={equipesModalOpen}
      onClose={() => setEquipesModalOpen(false)}
      startDate={startDate}
      endDate={endDate}
      contrato={contrato}
      centro={centro}
      equipe={equipe}
    />
    </>
  );
};

export default TempoSaidaBaseRankingTable; 